// HistoryItem Component - Expandable history item for conversation inspection

import { useState } from 'react';
import type { HistoryItem, HistoryItemType } from '../../types/agent';
import { formatCost, formatLatency, formatTokens } from '../../services/agent';
import styles from './HistoryItem.module.css';

// Icons
function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={`${styles.chevronIcon} ${expanded ? styles.expanded : ''}`}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function CodeIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

// Type labels and colors
const TYPE_LABELS: Record<HistoryItemType, string> = {
  user: 'User Message',
  assistant: 'Assistant Response',
  tool_call: 'Tool Call',
  tool_result: 'Tool Result',
  llm_call: 'LLM Call',
};

// Helper to format timestamp
function formatTimestamp(timestamp: string): string {
  // Server returns timestamps without 'Z' suffix, so handle both cases
  let dateStr = timestamp;
  if (timestamp.includes('T') && !timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
    dateStr = timestamp + 'Z';
  }
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// Helper to format elapsed time in a readable way
function formatElapsedTime(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  } else if (ms < 60000) {
    const seconds = (ms / 1000).toFixed(1);
    return `${seconds}s`;
  } else {
    const minutes = Math.floor(ms / 60000);
    const seconds = ((ms % 60000) / 1000).toFixed(0);
    return `${minutes}m ${seconds}s`;
  }
}

// Try to parse a string as JSON, returns parsed object or null if not valid JSON
function tryParseJson(str: string): unknown | null {
  if (typeof str !== 'string') return null;
  const trimmed = str.trim();
  // Only try to parse if it looks like JSON (starts with { or [)
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

// Render JSON with proper formatting, parsing nested JSON strings
function renderFormattedJson(data: unknown, indent = 0): React.ReactNode {
  const indentStr = '  '.repeat(indent);
  const nextIndentStr = '  '.repeat(indent + 1);

  if (data === null) return <span style={{ color: '#f472b6' }}>null</span>;
  if (data === undefined) return <span style={{ color: '#f472b6' }}>undefined</span>;

  if (typeof data === 'boolean') {
    return <span style={{ color: '#f472b6' }}>{String(data)}</span>;
  }

  if (typeof data === 'number') {
    return <span style={{ color: '#a78bfa' }}>{data}</span>;
  }

  if (typeof data === 'string') {
    // Try to parse as JSON and render as nested structure
    const parsed = tryParseJson(data);
    if (parsed !== null) {
      return renderFormattedJson(parsed, indent);
    }
    // Regular string - render with actual newlines preserved
    return (
      <span style={{ color: '#86efac' }}>
        "{data}"
      </span>
    );
  }

  if (Array.isArray(data)) {
    if (data.length === 0) return '[]';
    return (
      <>
        {'['}
        {'\n'}
        {data.map((item, i) => (
          <span key={i}>
            {nextIndentStr}{renderFormattedJson(item, indent + 1)}
            {i < data.length - 1 ? ',' : ''}
            {'\n'}
          </span>
        ))}
        {indentStr}{']'}
      </>
    );
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data);
    if (entries.length === 0) return '{}';
    return (
      <>
        {'{'}
        {'\n'}
        {entries.map(([key, value], i) => (
          <span key={key}>
            {nextIndentStr}<span style={{ color: '#93c5fd' }}>"{key}"</span>: {renderFormattedJson(value, indent + 1)}
            {i < entries.length - 1 ? ',' : ''}
            {'\n'}
          </span>
        ))}
        {indentStr}{'}'}
      </>
    );
  }

  return String(data);
}

interface HistoryItemProps {
  item: HistoryItem;
  isHighlighted?: boolean;
}

export function HistoryItemComponent({ item, isHighlighted }: HistoryItemProps) {
  const [expanded, setExpanded] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  // Build raw data object for deep inspection
  const rawData = {
    type: item.type,
    timestamp: item.timestamp,
    content: item.content,
    metadata: item.metadata,
    ...(item.requestMetadata && { request: item.requestMetadata }),
  };

  const typeClass = styles[`historyItem${item.type.charAt(0).toUpperCase() + item.type.slice(1).replace('_', '')}`];

  return (
    <div className={`${styles.historyItem} ${typeClass} ${isHighlighted ? styles.highlighted : ''}`}>
      {/* Level 1: Header - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={styles.historyItemHeader}
      >
        <div className={styles.headerLeft}>
          <span className={styles.typeLabel}>
            {item.type === 'llm_call' && item.metadata?.llm_call_index !== undefined
              ? `LLM Call #${item.metadata.llm_call_index + 1}`
              : TYPE_LABELS[item.type]}
          </span>
          {item.metadata?.tool_name && (
            <span className={styles.toolName}>{item.metadata.tool_name}</span>
          )}
          {item.type === 'llm_call' && item.metadata?.llm_model && (
            <span className={styles.llmModel}>{item.metadata.llm_model}</span>
          )}
        </div>
        <div className={styles.headerRight}>
          {/* Elapsed time badge - shows time since request started */}
          {item.elapsedStartMs !== undefined && (
            <span className={styles.elapsedBadge} title="Time since request started">
              @{formatElapsedTime(item.elapsedStartMs)}
              {item.metadata?.duration_ms !== undefined && (
                <span className={styles.durationBadge}>
                  +{formatElapsedTime(item.metadata.duration_ms)}
                </span>
              )}
            </span>
          )}
          {item.type === 'llm_call' && item.metadata?.llm_latency && (
            <span className={styles.llmLatency}>{formatLatency(item.metadata.llm_latency)}</span>
          )}
          <span className={styles.timestamp}>{formatTimestamp(item.timestamp)}</span>
          <ChevronIcon expanded={expanded} />
        </div>
      </button>

      {/* Level 2: Expanded content */}
      {expanded && (
        <div className={styles.historyItemContent}>
          {/* Content preview - skip for LLM calls as we show structured data instead */}
          {item.type !== 'llm_call' && (
            <div className={styles.contentPreview}>
              {(() => {
                // Try to parse and render as JSON for tool results
                const parsed = tryParseJson(item.content);
                if (parsed !== null) {
                  return <pre>{renderFormattedJson(parsed)}</pre>;
                }
                // Plain text content
                return <pre>{item.content}</pre>;
              })()}
            </div>
          )}

          {/* LLM Call specific display */}
          {item.type === 'llm_call' && item.metadata && (
            <div className={styles.llmCallDetails}>
              <div className={styles.llmCallGrid}>
                <div className={styles.llmCallStat}>
                  <span className={styles.llmCallStatLabel}>Model</span>
                  <span className={styles.llmCallStatValue}>{item.metadata.llm_model}</span>
                </div>
                <div className={styles.llmCallStat}>
                  <span className={styles.llmCallStatLabel}>Input Tokens</span>
                  <span className={styles.llmCallStatValue}>{formatTokens(item.metadata.llm_input_tokens || 0)}</span>
                </div>
                <div className={styles.llmCallStat}>
                  <span className={styles.llmCallStatLabel}>Output Tokens</span>
                  <span className={styles.llmCallStatValue}>{formatTokens(item.metadata.llm_output_tokens || 0)}</span>
                </div>
                <div className={styles.llmCallStat}>
                  <span className={styles.llmCallStatLabel}>Total Tokens</span>
                  <span className={styles.llmCallStatValue}>{formatTokens(item.metadata.llm_total_tokens || 0)}</span>
                </div>
                <div className={styles.llmCallStat}>
                  <span className={styles.llmCallStatLabel}>Cost</span>
                  <span className={styles.llmCallStatValue}>{formatCost(item.metadata.llm_cost || 0)}</span>
                </div>
                <div className={styles.llmCallStat}>
                  <span className={styles.llmCallStatLabel}>Latency</span>
                  <span className={styles.llmCallStatValue}>{formatLatency(item.metadata.llm_latency || 0)}</span>
                </div>
              </div>
              {item.metadata.llm_error && (
                <div className={styles.llmCallError}>
                  <span className={styles.llmCallErrorLabel}>Error:</span> {item.metadata.llm_error}
                </div>
              )}
            </div>
          )}

          {/* Request metadata for assistant responses */}
          {item.requestMetadata && (
            <div className={styles.requestMetadata}>
              <div className={styles.requestMetadataTitle}>Request Details</div>
              <div className={styles.requestMetadataContent}>
                <div>
                  <span className={styles.metadataLabel}>Message:</span>{' '}
                  {item.requestMetadata.user_message.substring(0, 100)}
                  {item.requestMetadata.user_message.length > 100 ? '...' : ''}
                </div>
                <div>
                  <span className={styles.metadataLabel}>MCP Servers:</span>{' '}
                  {item.requestMetadata.mcp_servers.join(', ') || 'None'}
                </div>
                <div>
                  <span className={styles.metadataLabel}>History Length:</span>{' '}
                  {item.requestMetadata.conversation_history_length} messages
                </div>
                <div>
                  <span className={styles.metadataLabel}>Available Tools:</span>{' '}
                  <span className={styles.toolsList}>
                    {item.requestMetadata.available_tools.length > 0
                      ? item.requestMetadata.available_tools.join(', ')
                      : 'None'}
                  </span>
                </div>
                <details className={styles.systemPromptDetails}>
                  <summary className={styles.systemPromptSummary}>System Prompt</summary>
                  <pre className={styles.systemPromptContent}>
                    {item.requestMetadata.system_prompt}
                  </pre>
                </details>
              </div>
            </div>
          )}

          {/* Metadata summary */}
          {item.metadata?.tokens && (
            <div className={styles.metricsRow}>
              Tokens: {formatTokens(item.metadata.tokens)} | Cost: {formatCost(item.metadata.cost || 0)} | Latency: {formatLatency(item.metadata.latency || 0)}
            </div>
          )}

          {/* Level 3: Raw data toggle */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowRaw(!showRaw); }}
            className={styles.rawDataToggle}
          >
            <CodeIcon />
            {showRaw ? 'Hide' : 'Show'} Raw Data
          </button>

          {/* Level 3: Full raw JSON data */}
          {showRaw && (
            <div className={styles.rawDataContainer}>
              <pre>{renderFormattedJson(rawData)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default HistoryItemComponent;
